<script setup lang="ts">
import type { SidebarProps } from '@/components/ui/sidebar';
import TeamSwitcher from '@/components/TeamSwitcher.vue';
import NavUser from '@/components/NavUser.vue';
import { useAccountStore } from '@/store/AccountStore';
import {
  Inbox,
  ChevronRight,
  CircleGauge,
  Bot,
  WandSparkles,
  Table,
  Route,
  Handshake,
  HeartHandshake,
  Database,
  HandCoins,
  SquareTerminal,
  SquarePercent,
  Boxes,
  SquareActivity,
} from 'lucide-vue-next';
import CompanyPlaceholder from '@/components/icons/CompanyPlaceholder.vue';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail, SidebarGroupLabel } from '@/components/ui/sidebar';
const props = withDefaults(defineProps<SidebarProps>(), {
  collapsible: 'icon',
});
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { computed, markRaw } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n({ useScope: 'global' });
const accountState = useAccountStore();
const data = {
  user: {
    name: accountState.account.currentUser?.name || '',
    email: accountState.account.currentUser?.email || '',
    avatar: accountState.account.avatarUrl,
  },
  teams: [
    {
      name: 'LBC Frakt',
      logo: markRaw(CompanyPlaceholder),
      plan: 'New Horizons',
    },
    {
      name: 'Åkeri 2',
      logo: markRaw(CompanyPlaceholder),
      plan: 'New Horizons',
    },
    {
      name: 'Åkeri 3',
      logo: markRaw(CompanyPlaceholder),
      plan: 'New Horizons',
    },
  ],
};

type NavSubItem = { title: string; url: Record<string, any>; disabled?: boolean };
type NavItem = {
  title?: string;
  group: string;
  icon?: any;
  isActive?: boolean;
  url?: Record<string, any>;
  disabled?: boolean;
  items?: NavSubItem[];
};

const groupedNav = computed(() => {
  const navMain: NavItem[] = [
    {
      title: t('sidebar.terminal'),
      group: t('sidebar.group.tools'),
      icon: SquareTerminal,
      isActive: false,
      url: { name: 'terminal' },
      // items: [
      //   {
      //     title: 'Chat - DEMO',
      //     url: { name: 'terminal' },
      //   },
      // ],
    },
    {
      title: t('sidebar.chat'),
      group: t('sidebar.group.tools'),
      icon: SquareTerminal,
      isActive: false,
      url: { name: 'chat' },
    },
    {
      title: t('sidebar.inbox'),
      group: t('sidebar.group.tools'),
      icon: Inbox,
      isActive: false,
      items: [
        {
          title: t('sidebar.incoming'),
          url: { name: 'inbox-incoming' },
        },
        {
          title: t('sidebar.overview'),
          url: { name: 'inbox-overview' },
        },
      ],
    },
    {
      title: t('sidebar.opportunities'),
      group: t('sidebar.group.automation'),
      icon: CircleGauge,
      isActive: false,
      items: [
        {
          title: t('sidebar.overview'),
          url: { name: 'opportunities-overview' },
        },
        {
          title: t('sidebar.routes'),
          url: { name: 'opportunities-routes' },
          disabled: true,
        },
        {
          title: t('sidebar.approved'),
          url: { name: 'home' },
          disabled: true,
        },
        {
          title: t('sidebar.source'),
          url: { name: 'home' },
          disabled: true,
        },
      ],
    },
    {
      title: t('sidebar.workflows'),
      group: t('sidebar.group.automation'),
      icon: WandSparkles,
      disabled: true,
      isActive: false,
      items: [
        {
          title: t('generic.unknown'),
          url: { name: 'home' },
        },
      ],
    },
    {
      title: t('sidebar.agents'),
      group: t('sidebar.group.automation'),
      icon: Bot,
      disabled: true,
      isActive: false,
      items: [
        {
          title: t('generic.unknown'),
          url: { name: 'home' },
        },
      ],
    },
    {
      title: t('sidebar.orders'),
      group: t('sidebar.group.workspace'),
      icon: Table,
      isActive: false,
      items: [
        {
          title: t('generic.all'),
          url: { name: 'orders' },
        },
      ],
    },

    {
      title: t('sidebar.routes'),
      group: t('sidebar.group.workspace'),
      icon: Route,
      items: [
        {
          title: t('generic.all'),
          url: {
            name: 'routes',
          },
        },
      ],
    },
    {
      title: t('sidebar.resources'),
      group: t('sidebar.group.workspace'),
      icon: Boxes,
      items: [
        {
          title: t('sidebar.vehicles'),
          url: { name: 'resource-vehicles' },
        },
        {
          title: t('sidebar.drivers'),
          url: { name: 'resource-drivers' },
        },
        {
          title: t('sidebar.schedules'),
          url: { name: 'resource-schedules' },
        },
        {
          title: t('sidebar.depots'),
          url: { name: 'resource-depots' },
        },
        {
          title: t('sidebar.capabilities'),
          url: { name: 'resource-capabilities' },
        },
        {
          title: t('sidebar.lineAssignments'),
          url: { name: 'resource-lineassignments' },
        },
        {
          title: t('sidebar.addresses'),
          url: { name: 'resource-addresses' },
        },
        {
          title: t('sidebar.operations'),
          url: { name: 'resource-operations' },
        },
        {
          title: t('sidebar.contacts'),
          url: { name: 'resource-contacts' },
        },
      ],
    },
    {
      title: t('sidebar.customers'),
      group: t('sidebar.group.workspace'),
      icon: HeartHandshake,
      items: [
        {
          title: t('generic.all'),
          url: { name: 'customers' },
        },
      ],
    },
    {
      title: t('sidebar.suppliers'),
      group: t('sidebar.group.workspace'),
      icon: Handshake,
      items: [
        {
          title: t('generic.all'),
          url: { name: 'suppliers' },
        },
      ],
    },
    {
      title: t('sidebar.articles'),
      group: t('sidebar.group.workspace'),
      icon: Database,
      items: [
        {
          title: t('generic.all'),
          url: { name: 'articles' },
        },
      ],
    },
    {
      title: t('sidebar.pricelists'),
      group: t('sidebar.group.workspace'),
      icon: SquarePercent,
      items: [
        {
          title: t('generic.all'),
          url: { name: 'pricelists' },
        },
      ],
    },
    // {
    //     title: "Avtal",
    //     url: { name: "home" },
    //     icon: ScrollText,
    //     items: [],
    // },
    {
      title: t('sidebar.invoices'),
      group: t('sidebar.group.workspace'),
      icon: HandCoins,
      items: [
        {
          title: t('generic.all'),
          url: { name: 'invoices' },
        },
      ],
    },
    {
      group: t('sidebar.group.settings'),
      title: t('settings.status'),
      icon: SquareActivity,
      isActive: false,
      url: { name: 'settings-status' },
    },
  ];

  const groups: Record<string, typeof navMain> = {};
  for (const item of navMain) {
    const groupName = item.group || 'Other';
    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(item);
  }
  return groups;
});
</script>

<template>
  <!-- Sidebar -->
  <Sidebar v-bind="props">
    <SidebarHeader>
      <TeamSwitcher :teams="data.teams" />
    </SidebarHeader>

    <SidebarContent>
      <template v-for="(groups, groupName) in groupedNav" :key="groupName">
        <SidebarGroup>
          <SidebarGroupLabel>{{ groupName }}</SidebarGroupLabel>
          <SidebarMenu>
            <template v-for="group in groups" :key="group.title">
              <SidebarMenuSubButton
                v-if="!group.items?.length"
                as-child
                :class="{ 'opacity-50 cursor-not-allowed': group.disabled, 'pointer-events-none': group.disabled }"
                :disabled="group.disabled"
              >
                <RouterLink :to="group.url?.name === $route.name ? { ...(group.url || {}), query: $route.query } : group.url || {}">
                  <component :is="group.icon" class="mr-2 h-4 w-4" />
                  <span>{{ group.title }}</span>
                </RouterLink>
              </SidebarMenuSubButton>
              <Collapsible
                v-else
                as-child
                :default-open="group.isActive"
                class="group/collapsible"
                :class="{ 'opacity-50 cursor-not-allowed': group.disabled, 'pointer-events-none': group.disabled }"
                :disabled="group.disabled"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger as-child>
                    <SidebarMenuButton :tooltip="group.title" class="group/menuitem flex items-center">
                      <component :is="group.icon" class="mr-2 h-4 w-4" />
                      <span>{{ group.title }}</span>
                      <ChevronRight
                        class="ml-auto opacity-0 transition-all duration-200 group-hover/menuitem:opacity-100 group-data-[state=open]/collapsible:opacity-100 group-data-[state=open]/collapsible:rotate-90"
                      />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <!-- Submenu content -->
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem v-for="subItem in group.items" :key="subItem.title">
                        <SidebarMenuSubButton
                          as-child
                          :class="{ 'opacity-50 cursor-not-allowed': subItem.disabled, 'pointer-events-none': subItem.disabled }"
                          :disabled="subItem.disabled"
                        >
                          <RouterLink :to="subItem.url.name === $route.name ? { ...subItem.url, query: $route.query } : subItem.url">
                            <span>{{ subItem.title }}</span>
                          </RouterLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </template>
          </SidebarMenu>
        </SidebarGroup>
      </template>
    </SidebarContent>

    <!-- Bottom section with logged-in user info -->
    <SidebarFooter>
      <NavUser :user="data.user" />
    </SidebarFooter>
    <SidebarRail />
  </Sidebar>
</template>
